import { IFilterInfo } from "components/Lean/LeanTable";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

const FILTER_PARAM_PREFIX = "f";
const FILTER_VALUE_SEPARATOR = "|";

/** Serializes/deserializes LeanTable filter state to/from URL search params.
 *
 * URL format: ?f0=val1|val2&f1=val3
 * Each param key is `f{columnIndex}`, values are pipe-separated.
 */
export function useUrlFilters(): [IFilterInfo[], (filters: IFilterInfo[]) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const filterInfos = useMemo((): IFilterInfo[] => {
    const result: IFilterInfo[] = [];
    for (const [key, value] of searchParams.entries()) {
      const match = key.match(/^f(\d+)$/);
      if (match) {
        const columnIndex = parseInt(match[1]);
        const filters = value.split(FILTER_VALUE_SEPARATOR).filter(Boolean);
        if (filters.length > 0) {
          result.push({ columnIndex, filters });
        }
      }
    }
    return result;
  }, [searchParams]);

  const setFilterInfos = useCallback(
    (newFilters: IFilterInfo[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // remove all existing filter params
          for (const key of [...next.keys()]) {
            if (/^f\d+$/.test(key)) next.delete(key);
          }
          // write new filter params
          for (const fi of newFilters) {
            if (fi.filters.length > 0) {
              next.set(`${FILTER_PARAM_PREFIX}${fi.columnIndex}`, fi.filters.join(FILTER_VALUE_SEPARATOR));
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  return [filterInfos, setFilterInfos];
}

/** Reads/writes a single string search param (e.g. `?q=...`). */
export function useUrlQuery(paramName = "q"): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get(paramName) ?? "";

  const setQuery = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set(paramName, value);
          } else {
            next.delete(paramName);
          }
          return next;
        },
        { replace: true }
      );
    },
    [paramName, setSearchParams]
  );

  return [query, setQuery];
}
